import { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Loader2, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import {
  handleEmailPasswordAuth
} from '../../services/Firebase/Login';

// Lazy import de Firebase Web SDK solo para web (evita cargar Google API en móviles)
// Estas variables se cargarán dinámicamente solo cuando se necesiten en web
let firebaseWebAuth: any = null;

interface FirebaseAuthFormProps {
  mode: 'signin' | 'signup';
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export const FirebaseAuthForm: React.FC<FirebaseAuthFormProps> = ({ 
  mode, 
  onSuccess, 
  onError 
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      // Validate email
      if (!email || !email.includes('@')) {
        throw new Error('Please enter a valid email address');
      }

      // Validate password
      if (!password || password.length < 6) {
        throw new Error('Password must be at least 6 characters');
      }
      
      // Note: Additional password requirements (uppercase, special characters, etc.)
      // may be enforced by Firebase Console password policy settings.
      // If you see "weak password" errors, check Firebase Console > Authentication > Settings > Password Policy

      console.log(`Starting ${mode === 'signup' ? 'registration' : 'login'} with email and password:`, {
        email,
        mode,
        isNative: Capacitor.isNativePlatform()
      });

      // Authenticate using the service
      const webAuth = Capacitor.isNativePlatform() ? undefined : (firebaseWebAuth || (await import('../../config/firebase')).auth);
      const result = await handleEmailPasswordAuth(email, password, mode, webAuth);
      
      if (result && result.user) {
        console.log(`${mode === 'signup' ? 'Registration' : 'Login'} successful`, { 
          mode: result.mode, 
          isNewUser: result.isNewUser 
        });
        onSuccess?.();
      }
    } catch (err: any) {
      console.error(`Error during ${mode === 'signup' ? 'registration' : 'login'}:`, {
        code: err.code,
        message: err.message,
        error: err,
        stack: err.stack
      });
      
      let errorMessage = mode === 'signup' 
        ? 'An error occurred while creating your account.'
        : 'There was an error signing in.';
      
      // Handle specific Firebase error codes
      if (err.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already registered. Please sign in instead.';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      } else if (err.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address.';
      } else if (err.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password. Please try again.';
      } else if (err.code === 'auth/invalid-credential') {
        // This error can occur for multiple reasons:
        // 1. Wrong password
        // 2. User doesn't exist (for signin)
        // 3. Email/password combination is incorrect
        if (mode === 'signin') {
          errorMessage = 'No account found with this email address. Please create an account first or check your email and password.';
        } else {
          errorMessage = 'Invalid email or password. Please check your credentials and try again.';
        }
      } else if (err.code === 'auth/weak-password') {
        // Check if Firebase provides more details about password requirements
        const firebaseMessage = err.message || '';
        if (firebaseMessage.includes('uppercase') || firebaseMessage.includes('capital')) {
          errorMessage = 'Password must contain at least one uppercase letter.';
        } else if (firebaseMessage.includes('special') || firebaseMessage.includes('non-alphanumeric')) {
          errorMessage = 'Password must contain at least one special character (e.g., ., !, @, #, $).';
        } else if (firebaseMessage.includes('lowercase')) {
          errorMessage = 'Password must contain at least one lowercase letter.';
        } else if (firebaseMessage.includes('number') || firebaseMessage.includes('numeric')) {
          errorMessage = 'Password must contain at least one number.';
        } else {
          errorMessage = 'Password is too weak. It must be at least 6 characters and may require uppercase letters, numbers, or special characters.';
        }
      } else if (err.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later.';
      } else if (err.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (err.message) {
        // Check if the error message contains useful information
        if (err.message.includes('Backend authentication failed')) {
          errorMessage = 'Server error. Please try again later.';
        } else if (err.message.includes('Failed to get authentication token')) {
          errorMessage = 'Authentication error. Please try again.';
        } else {
          errorMessage = err.message;
        }
      } else if (err.toString) {
        errorMessage = err.toString();
      }
      
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-4">
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email..."
              className="input-field w-full pl-10"
              required
              disabled={isLoading}
              autoComplete="email"
            />
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password..."
              className="input-field w-full pl-10 pr-10"
              required
              disabled={isLoading}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              minLength={6}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
              disabled={isLoading}
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
        {error && <p className="text-red-500 text-sm">{error}</p>}
        <button
          type="submit"
          className="primary-button flex items-center justify-center gap-2 w-full"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {mode === 'signup' ? 'Creating account...' : 'Signing in...'}
            </>
          ) : (
            <>
              {mode === 'signup' ? (
                <>
                  <Mail className="w-5 h-5" />
                  Create Account
                </>
              ) : (
                <>
                  <Mail className="w-5 h-5" />
                  Sign In
                </>
              )}
            </>
          )}
        </button>
      </form>
    </div>
  );
};

